import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { apiCall } from '../utils/api'
import { useToast } from '../context/ToastContext'
import { trackSignUp, setUserId } from '../utils/analytics'

export default function OAuthCallbackPage() {
  const [searchParams] = useSearchParams()
  const { login } = useAuth()
  const navigate = useNavigate()
  const { showToast } = useToast()
  const hasProcessed = useRef(false)

  useEffect(() => {
    // Prevent duplicate processing (React StrictMode runs effects twice)
    if (hasProcessed.current) {
      console.log('[OAuth Callback] Already processed, skipping...')
      return
    }

    const handleOAuthCallback = async () => {
      hasProcessed.current = true
      const token = searchParams.get('token')
      const provider = searchParams.get('provider')
      const email = searchParams.get('email')

      console.log('[OAuth Callback] Starting OAuth callback handling...')
      console.log('[OAuth Callback] Token:', token ? 'Present' : 'Missing')
      console.log('[OAuth Callback] Provider:', provider)
      console.log('[OAuth Callback] Email:', email)

      if (!token || !provider) {
        console.error('[OAuth Callback] Missing required parameters')
        showToast('error', 'Invalid OAuth callback parameters', 'Authentication Error')
        navigate('/')
        return
      }

      try {
        console.log('[OAuth Callback] Storing authToken in localStorage...')
        // Temporarily store token to make API call
        localStorage.setItem('authToken', token)

        console.log('[OAuth Callback] Fetching user data from API...')
        // Fetch full user info
        const userData = await apiCall('/v1/user/me')
        console.log('[OAuth Callback] User data received:', userData)

        // Log the user in using AuthContext
        console.log('[OAuth Callback] Calling login() in AuthContext...')
        login(token, userData)

        // Track sign-up conversion in Google Analytics
        if (userData.id) {
          setUserId(userData.id.toString())
          trackSignUp(provider, userData.id.toString())
        } else {
          trackSignUp(provider)
        }

        showToast('success', `Successfully logged in with ${provider}!`, 'Welcome!')

        console.log('[OAuth Callback] Redirecting to dashboard...')
        // Navigate to dashboard
        navigate('/dashboard')
      } catch (error) {
        console.error('[OAuth Callback] Error during OAuth callback:', error)

        // Even if user info fetch fails, we have the token and basic info
        const basicUserData = {
          email: email || 'user@example.com',
          username: email?.split('@')[0] || 'user'
        }

        console.log('[OAuth Callback] Using basic user data as fallback:', basicUserData)
        login(token, basicUserData)
        showToast('success', `Logged in with ${provider}!`, 'Welcome!')
        navigate('/dashboard')
      }
    }

    handleOAuthCallback()
  }, [searchParams, login, navigate, showToast])

  return (
    <div className="flex items-center justify-center min-h-screen bg-white dark:bg-gray-900">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
        <p className="text-lg text-gray-900 dark:text-white">Completing login...</p>
      </div>
    </div>
  )
}
